import {
  buyNowQueryParamsSessionStorage,
  type BuyNowQueryParamKey,
  urlParamKeys,
  urlParams,
} from '@ctct/infrastructure-layer';

import type { AddonsUrlParams } from '../managers/AddOnsManager/types';
import type {
  ContactsTierSelectUrlAdapter,
  ContactsTierSelectUrlParams,
} from '../managers/ContactsTierSelectManager/types';
import type { CurrencyUrlParams, CurrencyUrlAdapter } from '../managers/CurrencyManager/types';
import type { EmailPlansUrlAdapter, EmailPlansUrlParams } from '../managers/EmailPlansManager/types';
import type {
  InboundPromoCodesUrlParams,
  InboundPromoCodesUrlAdapter,
} from '../managers/InboundPromoCodesManager/types';
import type { NonProfitUrlParams, NonProfitUrlAdapter } from '../managers/NonProfitManager/types';
import type { ReturnUrlAdapter, ReturnUrlParams } from '../managers/PayflowTopBarManager/types';
import type { PaymentMethodsUrlAdapter, PaymentMethodsUrlParams } from '../managers/PaymentMethodsManager/types';
import type { PrepaySelectUrlAdapter, PrepaySelectUrlParams } from '../managers/PrepaySelectManager/types';
import type { PromoCodesUrlAdapter } from '../managers/PromoCodesManager/types';

export class CentralizedUrlAdapter
  implements
    EmailPlansUrlAdapter,
    InboundPromoCodesUrlAdapter,
    PromoCodesUrlAdapter,
    PaymentMethodsUrlAdapter,
    PrepaySelectUrlAdapter,
    ContactsTierSelectUrlAdapter,
    CurrencyUrlAdapter,
    NonProfitUrlAdapter,
    ReturnUrlAdapter
{
  private withBuyNowSessionFallback(urlValue: string | null, storageKey: BuyNowQueryParamKey): string | null {
    return urlValue ?? buyNowQueryParamsSessionStorage.getParam(storageKey);
  }

  public getEmailParams(): EmailPlansUrlParams {
    return {
      emailPlanType: this.withBuyNowSessionFallback(urlParams.getEmailPlanType(), urlParamKeys.planType),
      emailPlanId: this.withBuyNowSessionFallback(urlParams.getEmailPlanId(), urlParamKeys.emailPlanId),
      deleteEmailPlanId: () => urlParams.deleteEmailPlanId(),
    };
  }

  public getPromoCodesParams(): InboundPromoCodesUrlParams {
    return {
      promoCodes: urlParams.getPromoCodes(),
      deletePromoCode: (promoCode: string) => urlParams.deletePromoCode(promoCode),
    };
  }

  public getPaymentMethodsUrlParams(): PaymentMethodsUrlParams {
    return {
      deleteAmazonCheckoutSessionId: () => urlParams.deleteAmazonCheckoutSessionId(),
      deletePaypalToken: () => urlParams.deletePaypalToken(),
      getAmazonCheckoutSessionId: () => urlParams.getAmazonCheckoutSessionId(),
      getPaypalToken: () => urlParams.getPaypalToken(),
      getStripeIntentClientSecret: () => urlParams.getStripeIntentClientSecret(),
      getIfStripeTurnedOn: () => !!urlParams.getIfStripeTurnedOn(),
    };
  }

  public getPrepaySelectParams(): PrepaySelectUrlParams {
    return {
      paymentPeriod: this.withBuyNowSessionFallback(urlParams.getPaymentPeriod(), urlParamKeys.paymentPeriod),
      deletePaymentPeriod: () => urlParams.deletePaymentPeriod(),
    };
  }

  public getContactsTierSelectParams(): ContactsTierSelectUrlParams {
    return {
      contactCount: this.withBuyNowSessionFallback(urlParams.getContactCount(), urlParamKeys.contactCount),
      deleteContactCount: () => urlParams.deleteContactCount(),
    };
  }

  public getCurrencyParams(): CurrencyUrlParams {
    return {
      currency: this.withBuyNowSessionFallback(urlParams.getCurrency(), urlParamKeys.currency),
      deleteCurrency: () => urlParams.deleteCurrency(),
    };
  }

  public getNonProfitParams(): NonProfitUrlParams {
    return {
      nonprofit: this.withBuyNowSessionFallback(urlParams.getNonprofit(), urlParamKeys.nonprofit),
      deleteNonprofit: () => urlParams.deleteNonprofit(),
    };
  }

  public getAddonsParams(): AddonsUrlParams {
    return {
      addons: urlParams.getAddons(),
      deleteAddons: () => urlParams.deleteAddons(),
    };
  }

  public getReturnUrlParams(): ReturnUrlParams {
    return {
      returnUrl: urlParams.getReturnUrl(),
      deleteReturnUrl: () => urlParams.deleteReturnUrl(),
      returnLabel: urlParams.getReturnLabel(),
      deleteReturnLabel: () => urlParams.deleteReturnLabel(),
    };
  }
}
